import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect online/offline status with real internet connectivity checking
 * @returns {boolean} - Returns true if online, false if offline
 */
export const useOnlineStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const checkInProgressRef = useRef(false);
    const lastCheckTimeRef = useRef(0);
    const consecutiveFailuresRef = useRef(0);

    // Function to check actual internet connectivity
    const checkOnlineStatus = async () => {
        // Prevent multiple simultaneous checks
        if (checkInProgressRef.current) {
            return;
        }

        checkInProgressRef.current = true;
        const now = Date.now();
        lastCheckTimeRef.current = now;

        // First, check navigator.onLine - if false, we're definitely offline
        if (!navigator.onLine) {
            console.log('🔴 navigator.onLine is false - OFFLINE');
            consecutiveFailuresRef.current++;
            if (consecutiveFailuresRef.current >= 2) {
                setIsOnline(false);
            }
            checkInProgressRef.current = false;
            return false;
        }

        try {
            // Check actual connectivity by reaching our own backend, not a third-party
            // service - external domains (e.g. Google's DNS API) are frequently blocked
            // by exhibition/corporate WiFi proxies even when our own backend is reachable.
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                console.log('⏱️ Request timeout - assuming OFFLINE');
                controller.abort();
            }, 3000);

            const response = await fetch('/api/ping', {
                method: 'GET',
                cache: 'no-cache',
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Ping responded with status ${response.status}`);
            }

            // If we succeed, reset failure counter and set online
            consecutiveFailuresRef.current = 0;
            console.log('🟢 Internet connectivity check passed - ONLINE');
            setIsOnline(true);
            checkInProgressRef.current = false;
            return true;
        } catch (error) {
            // Only set offline after multiple consecutive failures to avoid false positives
            consecutiveFailuresRef.current++;

            console.log(`🔴 Internet connectivity check failed (attempt ${consecutiveFailuresRef.current}):`, error.name, '-', error.message);

            // Only set offline after 2 consecutive failures
            if (consecutiveFailuresRef.current >= 2) {
                console.log('⚠️ Multiple consecutive failures - Setting OFFLINE');
                setIsOnline(false);
            } else {
                console.log('ℹ️ Single failure detected - waiting for confirmation');
            }

            checkInProgressRef.current = false;
            return false;
        }
    };

    useEffect(() => {
        const handleOnline = () => {
            console.log('🟢 Browser online event triggered');
            consecutiveFailuresRef.current = 0;
            setIsOnline(true);
            checkOnlineStatus();
        };

        const handleOffline = () => {
            console.log('🔴 Browser offline event triggered');
            consecutiveFailuresRef.current = 999; // High number to immediately trigger offline
            setIsOnline(false);
        };

        // Add event listeners for online/offline events
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // More aggressive periodic connectivity check - every 2 seconds
        const intervalId = setInterval(() => {
            checkOnlineStatus();
        }, 2000);

        // Initial check on mount
        checkOnlineStatus();

        // Cleanup event listeners and interval on unmount
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            clearInterval(intervalId);
        };
    }, []);

    return isOnline;
};
